import { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { ChatwootWidget } from "@/components/ChatwootWidget";
import { useImpersonation } from "@/hooks/useImpersonation";

import Auth from "./pages/Auth";
import Signup from "./pages/Signup";
import BoxAnalyzer from "./pages/BoxAnalyzer";
import BoxAnalyzerResults from "./pages/BoxAnalyzerResults";
import QRCodeRouter from "./pages/QRCodeRouter";
import StorageOnboardingBridge from "./pages/StorageOnboardingBridge";
import GuidedStorageOnboarding from "./pages/GuidedStorageOnboarding";
import StorageOnboardingWelcome from "./pages/StorageOnboardingWelcome";
import ExpressPickupOnboarding from "./pages/ExpressPickupOnboarding";
import DashboardConnected from "./pages/DashboardConnected";
import BoxNewConnected from "./pages/BoxNewConnected";
import BoxDetailConnected from "./pages/BoxDetailConnected";
import SchedulePickupConnected from "./pages/SchedulePickupConnected";
import SchedulePickupSuccess from "./pages/SchedulePickupSuccess";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import NotFound from "./pages/NotFound";
import Settings from "./pages/Settings";
import Onboarding from "./pages/Onboarding";
import Admin from "./pages/Admin";
import AdminStripeProducts from "./pages/AdminStripeProducts";
import AdminAPISettings from "./pages/AdminAPISettings";
import AdminEmailTesting from "./pages/AdminEmailTesting";
import AdminMigrateBoxes from "./pages/AdminMigrateBoxes";
import AdminAddPrepaid from "./pages/AdminAddPrepaid";
import AdminUsers from "./pages/AdminUsers";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return session ? <>{children}</> : <Navigate to="/auth" replace />;
};

const AppContent = () => {
  const { isImpersonating, targetEmail, endImpersonation } = useImpersonation();
  
  return (
    <>
      <Toaster />
      <Sonner />
      {isImpersonating && targetEmail && (
        <ImpersonationBanner 
          targetUserEmail={targetEmail} 
          onExit={endImpersonation}
        />
      )}
      <Routes>
          <Route path="/" element={<Navigate to="/auth" replace />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/try" element={<BoxAnalyzer />} />
          <Route path="/try/results/:sessionId" element={<BoxAnalyzerResults />} />
          <Route path="/qr/:boxNumber" element={<QRCodeRouter />} />
          <Route path="/onboarding/storage" element={<StorageOnboardingBridge />} />
          <Route
            path="/onboarding/welcome"
            element={
              <ProtectedRoute>
                <StorageOnboardingWelcome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/express-pickup"
            element={
              <ProtectedRoute>
                <ExpressPickupOnboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/guided"
            element={
              <ProtectedRoute>
                <GuidedStorageOnboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardConnected />
              </ProtectedRoute>
            }
          />
          <Route
            path="/box/new"
            element={
              <ProtectedRoute>
                <BoxNewConnected />
              </ProtectedRoute>
            }
          />
          <Route
            path="/box/:id"
            element={
              <ProtectedRoute>
                <BoxDetailConnected />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedule-pickup"
            element={
              <ProtectedRoute>
                <SchedulePickupConnected />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedule-pickup/success"
            element={
              <ProtectedRoute>
                <SchedulePickupSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout/success"
            element={
              <ProtectedRoute>
                <CheckoutSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <Admin />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/stripe-products"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminStripeProducts />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/api-settings"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminAPISettings />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/email-testing"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminEmailTesting />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/migrate-boxes"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminMigrateBoxes />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          {/* Redirect old route */}
          <Route
            path="/admin/migrate-shopify-boxes"
            element={<Navigate to="/admin/migrate-boxes" replace />}
          />
          <Route
            path="/admin/add-prepaid"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminAddPrepaid />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminUsers />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      <ChatwootWidget />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
